import { Transaction } from 'components/TransactionAllocator';

import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import {
  ActionTransactionSubType,
  ActionTransactionSubTypes,
} from 'further-types/lp/action';
import { compact, sumBy } from 'lodash';
import buildRealisedGainsAndFxTransactions from './buildRealisedGainsAndFxTransactions';
import {
  HoldingClassInvestmentsConsiderationValues,
  HoldingClassRealisations,
} from '../../types';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { calculateTcyValue, CalendarDay } from 'further-ui/utils';
import getRealisationType from './helpers/getRealisationType';
import applyLcyBasedCurrencyLogic from './helpers/applyLcyBasedCurrencyLogic';
import { ConsiderationTypeHoldingClassTypes } from 'further-types/lp/realisation';

type BuildRealisationTransactionsInput = {
  defaultActionTransactions: Array<TransactionWithoutAmounts>;
  holdingClassRealisation: HoldingClassRealisations;
  realisationCurrencyRate: CurrencyRateDetails | null;
  threeCyRealisationCurrencyRate: CurrencyRateDetails | null;
  saleOfInvestment: Array<{
    investmentId: string;
    holdingClassId: string;
    investmentDate: CalendarDay;
    investmentSaleUnwindLcy: number;
    investmentSaleUnwindTcy: number;
  }>;
  unrealisedGainsUnwindPerHoldingClassInvestment: Array<{
    holdingClassId: string;
    investmentId: string;
    investmentDate: CalendarDay | undefined;
    unrealisedGainsUnwindAtDateLcy: number;
    unrealisedGainsUnwindAtDateTcy: number;
  }>;
  holdingClassInvestmentsConsiderationValues: Array<HoldingClassInvestmentsConsiderationValues>;
  realisationCurrencyCode: CurrencyCode;
  vehicleCurrencyCode: CurrencyCode;
};

const sameCurrencyCode = (
  holdingClassInvestments: Array<{ currency: CurrencyCode }>,
) => {
  return holdingClassInvestments.every(
    (investment) => investment.currency === holdingClassInvestments[0].currency,
  );
};

const buildRealisationTransactions = ({
  defaultActionTransactions,
  holdingClassRealisation,
  realisationCurrencyRate,
  unrealisedGainsUnwindPerHoldingClassInvestment,
  holdingClassInvestmentsConsiderationValues,
  realisationCurrencyCode,
  vehicleCurrencyCode,
  threeCyRealisationCurrencyRate,
  saleOfInvestment,
}: BuildRealisationTransactionsInput) => {
  const holdingClassInvestments = holdingClassRealisation.flatMap(
    (holdingClass) => holdingClass.unitsAllocationPerInvestment ?? [],
  );

  if (holdingClassInvestments.length === 0) {
    return [];
  }

  if (!sameCurrencyCode(holdingClassInvestments)) {
    throw new Error(
      'All holding class investments must have the same currency code',
    );
  }

  const realisationType = getRealisationType({
    holdingClassInvestment: holdingClassInvestments[0],
    vehicleCurrencyCode,
    realisationCurrencyCode,
  });

  const holdingClassCurrencyCode = holdingClassInvestments[0].currency;

  // Calculate total amounts for each transaction type
  const { cashProceedsTcy, escrowProceedsTcy, withholdingTaxTcy } =
    calculateTotalAmounts(holdingClassRealisation);

  const holdingClassInvestmentsWithConsiderations = holdingClassInvestments.map(
    (investment) => ({
      ...investment,
      ...holdingClassInvestmentsConsiderationValues.find(
        (consideration) =>
          consideration.investmentId === investment.investmentId &&
          consideration.holdingClassId === investment.holdingClassId,
      ),
    }),
  );

  const realisedGainsAndFxTransactions = buildRealisedGainsAndFxTransactions({
    defaultActionTransactions,
    holdingClassInvestments: holdingClassInvestmentsWithConsiderations.map(
      (investment) => ({
        ...investment,
        investmentSaleUnwindTcy:
          saleOfInvestment.find(
            (sale) =>
              sale.investmentId === investment.investmentId &&
              sale.holdingClassId === investment.holdingClassId,
          )?.investmentSaleUnwindTcy ?? 0,
        investmentSaleUnwindLcy:
          saleOfInvestment.find(
            (sale) =>
              sale.investmentId === investment.investmentId &&
              sale.holdingClassId === investment.holdingClassId,
          )?.investmentSaleUnwindLcy ?? 0,
        realisationCurrencyRate,
        currencyCode: realisationCurrencyCode,
        currency: investment.currency,
        investmentDate: investment.date,
        receivedConsiderationTotal: investment.receivedConsiderationTotal ?? 0,
        escrowConsiderationTotal: investment.escrowConsiderationTotal ?? 0,
        earnoutConsiderationTotal: investment.earnoutConsiderationTotal ?? 0,
        withholdingTaxPayable: investment.withholdingTaxPayable ?? 0,
      }),
    ),
    realisationCurrencyCode,
    realisationCurrencyRate,
    threeCyRealisationCurrencyRate,
    realisationType,
  });

  // Build each transaction
  const saleOfInvestmentTransaction = buildSaleOfInvestmentTransaction({
    defaultTransaction: findDefaultTransaction(
      defaultActionTransactions,
      ActionTransactionSubTypes.SaleOfInvestment,
    ),
    saleOfInvestment,
    transactionCurrencyRate: realisationCurrencyRate,
    realisationType,
    holdingClassCurrencyCode,
    realisationCurrencyCode,
  });

  const cashProceedsTransaction = buildCashProceedsTransaction({
    defaultTransaction: findDefaultTransaction(
      defaultActionTransactions,
      ActionTransactionSubTypes.CashProceeds,
    ),
    cashProceedsTcy,
    transactionCurrencyRate: realisationCurrencyRate,
    realisationType,
    holdingClassCurrencyCode,
    realisationCurrencyCode,
  });

  const escrowProceedsTransaction = buildEscrowProceedsTransaction({
    defaultTransaction: findDefaultTransaction(
      defaultActionTransactions,
      ActionTransactionSubTypes.EscrowProceeds,
    ),
    escrowProceedsTcy,
    transactionCurrencyRate: realisationCurrencyRate,
    realisationType,
    holdingClassCurrencyCode,
    realisationCurrencyCode,
  });

  const withholdingTaxTransaction = buildWithholdingTaxTransaction({
    defaultTransaction: findDefaultTransaction(
      defaultActionTransactions,
      ActionTransactionSubTypes.WithholdingTax,
    ),
    withholdingTaxTcy,
    transactionCurrencyRate: realisationCurrencyRate,
    realisationType,
    holdingClassCurrencyCode,
    realisationCurrencyCode,
  });

  const unrealisedGainsUnwindTransaction =
    buildUnrealisedGainsUnwindTransaction({
      defaultTransactions: defaultActionTransactions,
      transactionCurrencyRate: realisationCurrencyRate,
      unrealisedGainsUnwindPerHoldingClassInvestment,
      holdingClassCurrencyCode,
    });

  return compact([
    cashProceedsTransaction,
    escrowProceedsTransaction,
    saleOfInvestmentTransaction,
    withholdingTaxTransaction,
    realisedGainsAndFxTransactions,
    unrealisedGainsUnwindTransaction,
  ]).flat();
};

const calculateTotalAmounts = (
  holdingClassRealisation: BuildRealisationTransactionsInput['holdingClassRealisation'],
) => {
  return {
    cashProceedsTcy: sumBy(
      holdingClassRealisation,
      (holdingClass) =>
        holdingClass.cashConsiderationAmounts?.receivedConsiderationTotal ?? 0,
    ),
    escrowProceedsTcy: sumBy(
      holdingClassRealisation,
      (holdingClass) =>
        holdingClass.cashConsiderationAmounts?.escrowConsiderationTotal ?? 0,
    ),
    withholdingTaxTcy: sumBy(
      holdingClassRealisation,
      (holdingClass) =>
        holdingClass.cashConsiderationAmounts?.withholdingTaxPayable ?? 0,
    ),
  };
};

const findDefaultTransaction = (
  transactions: Array<TransactionWithoutAmounts>,
  subType: ActionTransactionSubType,
) =>
  transactions.find(
    (transaction) => transaction.transactionSubType === subType,
  );

type TransactionWithoutAmounts = Omit<
  Transaction,
  'amountExVatLcy' | 'amountIncVatLcy' | 'amountExVatTcy' | 'amountIncVatTcy'
>;

type SaleOfInvestmentInput = {
  defaultTransaction?: TransactionWithoutAmounts;
  saleOfInvestment: Array<{
    investmentId: string;
    holdingClassId: string;
    investmentDate: CalendarDay;
    investmentSaleUnwindLcy: number;
    investmentSaleUnwindTcy: number;
  }>;
  transactionCurrencyRate: CurrencyRateDetails | null;
  realisationType: ConsiderationTypeHoldingClassTypes;
  holdingClassCurrencyCode: CurrencyCode;
  realisationCurrencyCode: CurrencyCode;
};

const buildSaleOfInvestmentTransaction = ({
  saleOfInvestment,
  defaultTransaction,
  transactionCurrencyRate,
  realisationType,
  holdingClassCurrencyCode,
  realisationCurrencyCode,
}: SaleOfInvestmentInput) => {
  if (!defaultTransaction) {
    return null;
  }

  const transactions = saleOfInvestment.map((sale) => {
    return {
      ...(defaultTransaction ?? {}),
      operationType: TransactionOperation.Credit,
      amountExVatLcy: sale.investmentSaleUnwindLcy,
      amountIncVatLcy: sale.investmentSaleUnwindLcy,
      amountExVatTcy:
        realisationType === ConsiderationTypeHoldingClassTypes.LcyHoldingClass
          ? calculateTcyValue(
              sale.investmentSaleUnwindLcy,
              transactionCurrencyRate,
            )
          : sale.investmentSaleUnwindTcy,
      amountIncVatTcy:
        realisationType === ConsiderationTypeHoldingClassTypes.LcyHoldingClass
          ? calculateTcyValue(
              sale.investmentSaleUnwindLcy,
              transactionCurrencyRate,
            )
          : sale.investmentSaleUnwindTcy,
      transactionCurrency:
        realisationType ===
          ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass ||
        realisationType ===
          ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
          ? holdingClassCurrencyCode
          : realisationCurrencyCode,
      transactionCurrencyRate: transactionCurrencyRate ?? undefined,
      preCalculatedAmounts: true,
      investmentId: sale.investmentId,
      holdingClassId: sale.holdingClassId,
      label: sale.investmentDate.toLabel(),
    };
  });

  return transactions;
};

type CashProceedsInput = {
  defaultTransaction?: TransactionWithoutAmounts;
  cashProceedsTcy: number;
  transactionCurrencyRate: CurrencyRateDetails | null;
  realisationType: ConsiderationTypeHoldingClassTypes;
  holdingClassCurrencyCode: CurrencyCode;
  realisationCurrencyCode: CurrencyCode;
};

const buildCashProceedsTransaction = ({
  defaultTransaction,
  cashProceedsTcy,
  transactionCurrencyRate,
  realisationType,
  holdingClassCurrencyCode,
  realisationCurrencyCode,
}: CashProceedsInput) => {
  if (!defaultTransaction) {
    return null;
  }

  return {
    ...(defaultTransaction ?? {}),
    operationType: TransactionOperation.Debit,
    ...applyLcyBasedCurrencyLogic(
      cashProceedsTcy,
      transactionCurrencyRate,
      realisationType,
      holdingClassCurrencyCode,
      realisationCurrencyCode,
    ),
  };
};

type WithholdingTaxInput = {
  defaultTransaction?: TransactionWithoutAmounts;
  withholdingTaxTcy: number;
  transactionCurrencyRate: CurrencyRateDetails | null;
  realisationType: ConsiderationTypeHoldingClassTypes;
  holdingClassCurrencyCode: CurrencyCode;
  realisationCurrencyCode: CurrencyCode;
};

const buildWithholdingTaxTransaction = ({
  defaultTransaction,
  withholdingTaxTcy,
  transactionCurrencyRate,
  realisationType,
  holdingClassCurrencyCode,
  realisationCurrencyCode,
}: WithholdingTaxInput) => {
  if (!defaultTransaction) {
    return null;
  }

  return {
    ...(defaultTransaction ?? {}),
    operationType: TransactionOperation.Debit,
    ...applyLcyBasedCurrencyLogic(
      withholdingTaxTcy,
      transactionCurrencyRate,
      realisationType,
      holdingClassCurrencyCode,
      realisationCurrencyCode,
    ),
  };
};

type EscrowProceedsInput = {
  defaultTransaction?: TransactionWithoutAmounts;
  escrowProceedsTcy: number;
  transactionCurrencyRate: CurrencyRateDetails | null;
  realisationType: ConsiderationTypeHoldingClassTypes;
  holdingClassCurrencyCode: CurrencyCode;
  realisationCurrencyCode: CurrencyCode;
};

const buildEscrowProceedsTransaction = ({
  defaultTransaction,
  escrowProceedsTcy,
  transactionCurrencyRate,
  realisationType,
  holdingClassCurrencyCode,
  realisationCurrencyCode,
}: EscrowProceedsInput) => {
  if (!defaultTransaction) {
    return null;
  }

  return {
    ...(defaultTransaction ?? {}),
    operationType: TransactionOperation.Debit,
    ...applyLcyBasedCurrencyLogic(
      escrowProceedsTcy,
      transactionCurrencyRate,
      realisationType,
      holdingClassCurrencyCode,
      realisationCurrencyCode,
    ),
  };
};

type UnrealisedGainsUnwindInput = {
  defaultTransactions?: Array<TransactionWithoutAmounts>;
  unrealisedGainsUnwindPerHoldingClassInvestment: Array<{
    holdingClassId: string;
    investmentId: string;
    investmentDate: CalendarDay | undefined;
    unrealisedGainsUnwindAtDateLcy: number;
    unrealisedGainsUnwindAtDateTcy: number;
  }>;
  transactionCurrencyRate: CurrencyRateDetails | null;
  holdingClassCurrencyCode: CurrencyCode;
};

const buildUnrealisedGainsUnwindTransaction = ({
  defaultTransactions,
  unrealisedGainsUnwindPerHoldingClassInvestment,
  holdingClassCurrencyCode,
}: UnrealisedGainsUnwindInput) => {
  if (
    !defaultTransactions ||
    unrealisedGainsUnwindPerHoldingClassInvestment.length === 0
  ) {
    return null;
  }

  const transactions = unrealisedGainsUnwindPerHoldingClassInvestment.map(
    (holdingClassInvestmentUnwind) => {
      const unrealisedGainsUnwindTcy =
        holdingClassInvestmentUnwind.unrealisedGainsUnwindAtDateTcy;
      const unrealisedGainsUnwindLcy =
        holdingClassInvestmentUnwind.unrealisedGainsUnwindAtDateLcy;

      const transactionType =
        unrealisedGainsUnwindTcy > 0
          ? ActionTransactionSubTypes.UnrealisedGainsUnwind
          : ActionTransactionSubTypes.UnrealisedLossesUnwind;

      // Find the specific debit and credit transactions
      const debitTransaction = defaultTransactions.find(
        (transaction) =>
          transaction.transactionSubType === transactionType &&
          transaction.operationType === TransactionOperation.Debit,
      );

      const creditTransaction = defaultTransactions.find(
        (transaction) =>
          transaction.transactionSubType === transactionType &&
          transaction.operationType === TransactionOperation.Credit,
      );

      const transactionsForThisItem: Array<
        Transaction & { holdingClassId: string; investmentId: string }
      > = [];

      if (debitTransaction) {
        transactionsForThisItem.push({
          ...(debitTransaction ?? {}),
          amountExVatLcy: Math.abs(unrealisedGainsUnwindLcy),
          amountIncVatLcy: Math.abs(unrealisedGainsUnwindLcy),
          amountExVatTcy: Math.abs(unrealisedGainsUnwindTcy),
          amountIncVatTcy: Math.abs(unrealisedGainsUnwindTcy),
          preCalculatedAmounts: true,
          transactionCurrency: holdingClassCurrencyCode,
          label: `${holdingClassInvestmentUnwind.investmentDate?.toLabel()}`,
          holdingClassId: holdingClassInvestmentUnwind.holdingClassId,
          investmentId: holdingClassInvestmentUnwind.investmentId,
        });
      }

      if (creditTransaction) {
        transactionsForThisItem.push({
          ...(creditTransaction ?? {}),
          amountExVatLcy: Math.abs(unrealisedGainsUnwindLcy),
          amountIncVatLcy: Math.abs(unrealisedGainsUnwindLcy),
          amountExVatTcy: Math.abs(unrealisedGainsUnwindTcy),
          amountIncVatTcy: Math.abs(unrealisedGainsUnwindTcy),
          preCalculatedAmounts: true,
          transactionCurrency: holdingClassCurrencyCode,
          label: `${holdingClassInvestmentUnwind.investmentDate?.toLabel()}`,
          holdingClassId: holdingClassInvestmentUnwind.holdingClassId,
          investmentId: holdingClassInvestmentUnwind.investmentId,
        });
      }

      return transactionsForThisItem;
    },
  );

  return transactions.flat();
};

export default buildRealisationTransactions;
